
import { Context } from "@nuxt/types";
import {
  AppointmentConfig,
  Clinic,
  AppointmentSchedule,
} from "@ambii-com/core";
import Vue from "vue";
import { mapActions } from "vuex";
import { fsLocations } from "~/dao/FsLocations";
import { Menu } from "@ambii-com/core";
import SubmenuDialog from "~/components/pages/appointment/SubmenuDialog.vue";
import NoticeDialog from "~/components/pages/appointment/NoticeDialog.vue";
import PatientCountDialog from "~/components/pages/appointment/PatientCountDialog.vue";
import NoSlotsWarning from "~/components/pages/appointment/NoSlotsWarning.vue";
import TermsOfServices from "~/pages/appointment/termsOfServices.vue";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(timezone);
dayjs.extend(utc);

export default Vue.extend({
  components: {
    SubmenuDialog,
    NoticeDialog,
    PatientCountDialog,
    TermsOfServices,
    NoSlotsWarning,
  },
  middleware: ["restrictClinicAccess"],
  data(): {
    termsOfServiceSheet: Boolean;
    noticeDialog: Boolean;
    submenuDialog: Boolean;
    patientCountDialog: Boolean;
    selectedMenu: Menu | null;
    selectedSubmenu: Menu | null;
    patientCount: number | null;
    locale: string;
    noSlotsDialog: boolean;
    hasShownWarning: boolean;
    hover: Boolean;
    hoveredMenu: string | null;
    clinic: Clinic | null;
    menus: Menu[];
    submenus: Menu[];
    appointmentConfig: AppointmentConfig | null;
  } {
    return {
      termsOfServiceSheet: false,
      noticeDialog: false,
      submenuDialog: false,
      patientCountDialog: false,
      selectedMenu: null,
      selectedSubmenu: null,
      patientCount: null,
      locale: this.$i18n.locale,
      noSlotsDialog: false,
      hasShownWarning: false,
      hover: false,
      hoveredMenu: null,
      clinic: null,
      menus: [],
      submenus: [],
      appointmentConfig: null,
    };
  },
  async asyncData({ route, store }: Context) {
    const clinicId = route.params["clinicId"];
    const clinicData = await fsLocations.clinicsRef
      .doc(clinicId)
      .get()
      .then(Clinic.fromDoc);

    const clinic = new Clinic(
      clinicData.id,
      clinicData.name,
      clinicData.language,
      clinicData.timeLocale,
      clinicData.activeServices,
      clinicData.formConfigId,
      clinicData.appointmentConfigId,
      clinicData.createdAt,
      clinicData.updatedAt,
      clinicData.version,
      clinicData.integrateWithAlleyPin,
      clinicData.alleyPinServiceStoreId,
      clinicData.phoneCountryCode,
      clinicData.region
    );

    const appointmentConfig = await fsLocations.appointmentConfigsRef
      .doc(clinic.appointmentConfigId)
      .get()
      .then(AppointmentConfig.fromDoc);
    const allMenus = await fsLocations
      .getMenusForPatientRefByClinicId(clinicId)
      .get()
      .then(({ docs }) => Menu.fromDocs(docs));
    const menus = allMenus.filter((menu) => !menu.isSubmenu);
    const submenus = allMenus.filter((menu) => menu.isSubmenu);
    store.dispatch("auth/setClinic", clinic);
    return { clinic, menus, submenus, appointmentConfig };
  },
  computed: {
    clinicImageSrc(): string {
      return require(
        `@/assets/images/clinic-image${this.appointmentConfig.startBackgroundImg || "1"}.jpg`,
      );
    },
    startTitle(): string {
      return this.appointmentConfig.startTitle || `${this.clinic.name} 予約`;
    },
    startSubtitle(): string {
      const config: AppointmentConfig = this.appointmentConfig;
      return config.startSubtitle || this.$t("app.start.selectMenu");
    },
    dialogWidth(): string {
      return this.$vuetify.breakpoint.xs ? "100%" : "40%";
    },
    hideSubmenuDialogClass(): string {
      return this.noticeDialog || this.noSlotsDialog || this.patientCountDialog
        ? "d-none"
        : "";
    },
    hideNoticeDialogClass(): string {
      return this.noSlotsDialog ? "d-none" : "";
    },
    hidePatientCountDialogClass(): string {
      return this.noticeDialog || this.noSlotsDialog ? "d-none" : "";
    },
    noticeHTML(): string | undefined {
      if (this.selectedSubmenu) {
        return (this.selectedSubmenu as Menu).localizedNoticeHTML(this.locale);
      } else if (this.selectedMenu) {
        return (this.selectedMenu as Menu).localizedNoticeHTML(this.locale);
      }
    },
    submenusOfSelectedMenu(): Menu[] {
      return this.submenus.filter(
        (submenu: Menu) => submenu.parentMenuId === this.selectedMenu.id,
      );
    },
    currentYear(): number {
      return new Date().getFullYear();
    },
    isMobile(): boolean {
      return this.$vuetify.breakpoint.smAndDown;
    },
  },
  methods: {
    ...mapActions("appointmentForm", [
      "setAppointmentPatientCount",
      "resetAppointmentForms",
      "resetAppointmentTimeSlots",
      "setSelectedRelatedPatients",
    ]),
    selectMenu(menu: Menu): void {
      this.selectedMenu = menu;
      if (this.submenusOfSelectedMenu.length > 0) {
        this.submenuDialog = true;
      } else {
        this.goToDateSelect();
      }
    },
    selectPatientCount(): void {
      if (this.submenusOfSelectedMenu.length > 0) {
        this.submenuDialog = true;
      } else {
        this.goToDateSelect();
      }
    },
    selectSubmenu(submenu: Menu): void {
      this.selectedSubmenu = submenu;
    },
    setPatientCount(count: number | null): void {
      this.patientCount = count;
    },
    hasNotice(): boolean {
      return !!this.noticeHTML && !this.noticeDialog;
    },
    async hasVacancy(): Promise<boolean> {
      const menuForSchedule = this.selectedSubmenu || this.selectedMenu;
      if (!menuForSchedule) return false;
      const querySnapshot = await fsLocations
        .getVacantSchedulesRef(menuForSchedule.id)
        .get();
      if (querySnapshot.size == 0) {
        return false;
      } else {
        let hasVacancy = false;
        const now = new Date(dayjs().tz(this.clinic?.timeZone).format("YYYY-MM-DD HH:mm:ss"));
        for (const doc of querySnapshot.docs) {
          const schedule = AppointmentSchedule.fromDoc(doc as any);
          const isWithinReceptionTime = (
            new Date(dayjs(schedule.receptionStartDatetime).tz(this.clinic?.timeZone).format("YYYY-MM-DD HH:mm:ss")) <= now &&
            now < new Date(dayjs(schedule.receptionEndDatetime).tz(this.clinic?.timeZone).format("YYYY-MM-DD HH:mm:ss"))
          );
          const hasAvailableSlots = schedule.timeSlots.some(
            (timeSlot) => timeSlot.maxNumber - timeSlot.appliedNumber > 0,
          );
          const intervalMillis = schedule.intervalMinutes * 60 * 1000;
          let slotStartTime = now;
          // This loop calculates the start time of the very last slot in the schedule,
          for (let i = schedule.timeSlots.length - 1; i >= 0; i--) {
            const scheduleStartDateTime = new Date(dayjs(schedule.startDatetime).tz(this.clinic?.timeZone).format("YYYY-MM-DD HH:mm:ss"));
            slotStartTime = new Date(
              scheduleStartDateTime.getTime() + i * intervalMillis,
            );
            break; // Exit after the first iteration (calculating the last slot's start time)
          }

          // Check if the current time is before the start time of the last slot calculated above.
          const isBeforeReceptionStart =
            now.getTime() < slotStartTime.getTime();
          if (
            hasAvailableSlots &&
            isWithinReceptionTime &&
            isBeforeReceptionStart
          ) {
            hasVacancy = true;
            break;
          }
        }
        return hasVacancy;
      }
    },
    async goToDateSelect(): Promise<void> {
      if (this.selectedMenu?.isMultiplePatients && !this.patientCount) {
        this.patientCountDialog = true;
        this.patientCount = 1;
        return;
      }
      // Make sure that the order of the if statements is correct.
      // EX: hasNotice needs to be checked first, otherwise in the case where
      // there is a notice and has vacancy, this.$router.push will be called
      // before the hasNotice check, resulting in the notice dialog not being shown
      if (this.hasNotice()) {
        this.noticeDialog = true;
      } else if (await this.hasVacancy()) {
        this.setAppointmentPatientCount(this.patientCount ?? 1);
        this.$store.dispatch("appointmentForm/resetAppointmentTimeSlots");
        const menuForSchedule = !!this.selectedSubmenu
          ? this.selectedSubmenu
          : this.selectedMenu;
        this.$router.push(
          this.localePath(`/appointment/menu/${menuForSchedule.id}`),
        );
      } else {
        if (!this.hasShownWarning) {
          this.noSlotsDialog = true;
          this.hasShownWarning = true;
        } else {
          const menuForSchedule = this.selectedSubmenu || this.selectedMenu;
          this.$router.push(
            this.localePath(`/appointment/menu/${menuForSchedule.id}`),
          );
        }
      }
    },
    closeNoSlotsDialog(): void {
      this.noSlotsDialog = false;
      this.hasShownWarning = false;
    },
  },
  async mounted() {
    document.title = (await this.clinic)
      ? `${this.clinic.name} WEB予約 - Ambii`
      : "Ambii WEB予約";
    // Reset appointment form state
    this.resetAppointmentTimeSlots();
    this.setSelectedRelatedPatients([]);
    this.resetAppointmentForms();
  },
});
